import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  FileText, 
  Upload, 
  Download, 
  Trash2, 
  Loader2,
  File,
  FileImage,
  FileType,
  User
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { id: "id_copy", name: "ID Copy", icon: User },
  { id: "contract", name: "Employment Contract", icon: FileText },
  { id: "certificate", name: "Certificate/Qualification", icon: File },
  { id: "custom", name: "Other Document", icon: File }
];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(2) + " MB";
};

const getFileIcon = (mimeType) => {
  if (mimeType?.includes("pdf")) return FileType;
  if (mimeType?.includes("image")) return FileImage;
  return FileText;
};

export const DocumentsTab = ({ employees }) => {
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [documentType, setDocumentType] = useState("id_copy");
  const [documentName, setDocumentName] = useState("");

  useEffect(() => {
    if (selectedEmployee) {
      fetchDocuments();
    }
  }, [selectedEmployee]);

  const fetchDocuments = async () => {
    if (!selectedEmployee) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/employees/documents/${selectedEmployee}`);
      setDocuments(response.data || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !documentName || !selectedEmployee) {
      toast.error("Please fill in all fields");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("document_type", documentType);
      formData.append("document_name", documentName);
      formData.append("uploaded_by", "Admin");

      await axios.post(`${API}/employees/documents/${selectedEmployee}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Document uploaded successfully");
      setUploadDialogOpen(false);
      setUploadFile(null);
      setDocumentName("");
      setDocumentType("id_copy");
      fetchDocuments();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/employees/documents/${selectedEmployee}/${doc.id}`);
      const fileData = response.data.file_data;
      
      // Convert base64 to blob
      const byteCharacters = atob(fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.mime_type });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = response.data.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.document_name}"?`)) return;

    try {
      await axios.delete(`${API}/employees/documents/${selectedEmployee}/${doc.id}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
    }
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : "";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Employee Documents
          </CardTitle>
          <Button 
            onClick={() => setUploadDialogOpen(true)}
            disabled={!selectedEmployee}
            data-testid="upload-document-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-6">
          <Label>Select Employee</Label>
          <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
            <SelectTrigger className="w-full max-w-md" data-testid="select-employee-docs">
              <SelectValue placeholder="Choose an employee..." />
            </SelectTrigger>
            <SelectContent>
              {employees.filter(e => e.is_active).sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name} ({emp.employee_number})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedEmployee && (
          <>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No documents uploaded for {getEmployeeName(selectedEmployee)}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>File Name</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => {
                    const FileIcon = getFileIcon(doc.mime_type);
                    const docType = DOCUMENT_TYPES.find(t => t.id === doc.document_type);
                    
                    return (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileIcon className="h-4 w-4 text-gray-500" />
                            {doc.document_name}
                          </div>
                        </TableCell>
                        <TableCell>{docType?.name || doc.document_type}</TableCell>
                        <TableCell className="text-sm text-gray-500">{doc.file_name}</TableCell>
                        <TableCell className="text-sm">{formatFileSize(doc.file_size)}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload(doc)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(doc)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </CardContent>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Employee</Label>
              <Input 
                value={getEmployeeName(selectedEmployee)} 
                disabled 
                className="bg-gray-50"
              />
            </div>
            
            <div>
              <Label>Document Type</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...DOCUMENT_TYPES].sort((a, b) => (a.label || '').localeCompare(b.label || '')).map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Document Name</Label>
              <Input 
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="e.g., ID Copy - Front"
              />
            </div>
            
            <div>
              <Label>File (max 10MB)</Label>
              <Input 
                type="file"
                onChange={(e) => setUploadFile(e.target.files[0])}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              />
              {uploadFile && (
                <p className="text-sm text-gray-500 mt-1">
                  Selected: {uploadFile.name} ({formatFileSize(uploadFile.size)})
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile || !documentName}>
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DocumentsTab;
